import { NavTab, NavTabItem, ProgressBar, shapes, Toggle } from '@cimpress/react-components';
import momentTimezone from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { useInterval } from 'react-use';
import { useWorkflow } from '../../../adapters/use-workflow';
import CopyComponent from '../../utils/CopyComponent';
import { LocaleSettings, Location } from '../../../models/location';
import { UseWorkflowHook, UseWorkflowLogHook, Workflow, WorkflowStatus } from '../../../ports/WorkflowService';
import { useCurrentLocation } from '../../../store/hooks/useCurrentLocation';
import { AlignItems, FlexBox, FlexDirection, FlexGrow } from '../../../utils/css-classes';
import { BulkShipmentsWorkflowInput } from './BulkShipmentsWorkflowInput';
import { createWorkflowLog } from './WorkflowLog';
import { colors } from '../colors';
import { useWorkflowLog } from '../../../adapters/use-workflow-log';

// tslint:disable-next-line:import-name
import style from './index.module.css';

const { Spinner, Robot } = shapes;

// tslint:disable-next-line:function-name
function WorkflowInformation(
  {
    localeSettings,
    workflow,
  }: {
    localeSettings: LocaleSettings;
    workflow: Workflow;
  },
) {
  return <div>
    <h6>Progress</h6>
    {workflow.progress?.percentage ?? 0}%{workflow.progress ? ` - ${workflow.progress.summary}` : ''}
    <ProgressBar
      color={`var(--${colors[workflow.status]})`}
      size="l"
      value={workflow.progress?.percentage ?? 0}
    />
    <h6>Name</h6>
    {workflow.name}
    <div className={`${FlexBox.FLEX} ${FlexDirection.ROW}`}>
      <div className={FlexGrow.GROW_1}>
        <h6>Status</h6>
        {workflow.status}
      </div>
      <div className={FlexGrow.GROW_1}>
        <h6>Has Warnings</h6>
        {workflow.hasWarnings ? 'yes' : 'no'}
      </div>
      <div className={FlexGrow.GROW_1}>
        <h6>Has Errors</h6>
        {workflow.hasErrors ? 'yes' : 'no'}
      </div>
    </div>
    <h6>Created by</h6>
    {workflow.createdBy}
    <h6>Creation Date</h6>
    {momentTimezone(workflow.createdAt).tz(localeSettings.timezone).format(`${localeSettings.dateFormat} [at] HH:mm`)}
    <h6>Last Update Date</h6>
    {momentTimezone(workflow.updatedAt).tz(localeSettings.timezone).format(`${localeSettings.dateFormat} [at] HH:mm`)}
    <h6>Id</h6>
    {workflow.id}
    <CopyComponent value={workflow.id}/>
    <h6>Correlation Id</h6>
    {workflow.correlationId}
    <CopyComponent value={workflow.correlationId}/>
  </div>;
}

export function createWorkflowDetailsModal(
  {
    useCurrentLocation,
    useWorkflow,
    useWorkflowLog,
  }: {
    useCurrentLocation: () => Location;
    useWorkflow: UseWorkflowHook;
    useWorkflowLog: UseWorkflowLogHook;
  },
) {
  // tslint:disable-next-line:variable-name
  const WorkflowLog = createWorkflowLog({ useWorkflowLog, useCurrentLocation });

  // tslint:disable-next-line:function-name
  return function WorkflowDetailsModal(
    {
      workflowId,
    }: {
      workflowId: string;
    },
  ) {
    const { workflow, isLoading, update } = useWorkflow(workflowId);
    const { localeSettings } = useCurrentLocation();
    const [tab, setTab] = useState<'information' | 'input' | 'log'>('information');

    const [isRefreshingInternal, setIsRefreshing] = useState(true);
    const isRefreshing = useMemo(
      () => isRefreshingInternal &&
        !!workflow &&
        [WorkflowStatus.CREATED, WorkflowStatus.RUNNING].includes(workflow.status),
      [workflow, isRefreshingInternal],
    );

    useInterval(update, isRefreshing ? 1500 : null);

    if (isLoading && !workflow) {
      return <Spinner/>;
    }

    if (!workflow) {
      return <Robot bsStyle="danger" size="sm" />;
    }

    return <>
      <div className={`${style.Header} ${FlexBox.FLEX} ${FlexDirection.ROW} ${AlignItems.CENTER}`}>
        <NavTab className={FlexGrow.GROW_1}>
          <NavTabItem active={tab === 'information'}>
            <button onClick={() => setTab('information')}>Information</button>
          </NavTabItem>
          <NavTabItem active={tab === 'input'}>
            <button onClick={() => setTab('input')}>Input</button>
          </NavTabItem>
          <NavTabItem active={tab === 'log'}>
            <button onClick={() => setTab('log')}>Logs</button>
          </NavTabItem>
        </NavTab>
        <h6>
          Auto-refresh&nbsp;
        </h6>
        <Toggle on={isRefreshingInternal} onClick={() => setIsRefreshing(!isRefreshingInternal)} size="sm" />
      </div>
      {tab === 'information' && <WorkflowInformation workflow={workflow} localeSettings={localeSettings}/>}
      {tab === 'input' && <div>
        <BulkShipmentsWorkflowInput workflow={workflow} />
      </div>}
      {tab === 'log' && <WorkflowLog workflowId={workflow.id} isRefreshing={isRefreshing}/>}
    </>;
  };
}

// tslint:disable-next-line:variable-name
export const WorkflowDetailsModal = createWorkflowDetailsModal({ useWorkflow, useCurrentLocation, useWorkflowLog });
